<template>
  <div class="sms-templates">
    <filter-bar
      :filters="filterSchema"
      :initial-value="filterValues"
      :result-count="totalRows"
      :per-page="perPage"
      :per-page-options="perPageOptions"
      saved-filters-key="sms-templates"
      @change="onFiltersChanged"
      @update:perPage="onPerPageChange"
    >
      <template #actions>
        <base-button icon class="btn-link text-white" @click="downloadCSV">
          <i class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>
    </filter-bar>

    <loading-panel :loading="loading"></loading-panel>

    <!-- Empty state: no templates match -->
    <div v-if="!loading && templates.length === 0" class="sms-empty-state mt-4">
      <img src="/img/pandas/sms.png" alt="No SMS templates" class="empty-state-img" />
      <h4 class="empty-state-text">{{ hasActiveFilters ? 'No templates match these filters' : 'No SMS templates yet' }}</h4>
      <p class="sms-empty-state__hint">
        {{ hasActiveFilters
          ? 'Try clearing a filter to see more templates'
          : 'Templates are the text messages your automations send, like booking confirmations and reminders' }}
      </p>
    </div>

    <!-- Desktop table -->
    <div v-if="!loading && templates.length > 0" class="d-none d-sm-block">
      <b-table striped hover :items="templates" :fields="tableFields">
        <template #cell(template_info)="row">
          <div class="sms-templates__name">{{ row.item.name }}</div>
        </template>

        <template #cell(type)="row">
          <badge :style="typeBadgeStyle(row.item)">{{ row.item.type_name || 'General' }}</badge>
        </template>

        <template #cell(status)="row">
          <badge :type="row.item.status === 'active' ? 'success' : 'default'">
            {{ row.item.status }}
          </badge>
        </template>

        <!-- Mirrors the email templates Usage cell. SMS has no read receipts,
             so the second line is the delivery rate, not an open rate. -->
        <template #cell(usage)="row">
          <div class="small">
            <div><i class="tim-icons icon-chat-33 mr-1"></i>{{ row.item.sent_count || 0 }} sent</div>
            <div v-if="typeof row.item.delivery_rate === 'number'">
              <i class="tim-icons icon-chart-pie-36 mr-1"></i>{{ row.item.delivery_rate }}% delivered
            </div>
            <div v-else-if="row.item.sent_count > 0">
              <i class="tim-icons icon-chart-pie-36 mr-1"></i>Delivery pending
            </div>
          </div>
        </template>

        <template #cell(segments)="row">
          <span class="sms-templates__segments">
            {{ segmentsFor(row.item.body).segments }} segment{{ segmentsFor(row.item.body).segments === 1 ? '' : 's' }}
          </span>
        </template>

        <template #cell(last_modified)="row">
          <span class="sms-templates__meta">{{ formatDate(row.item.updated_at || row.item.created_at) }}</span>
        </template>

        <template #cell(actions)="row">
          <!-- Markup copied from the email templates menu in CustomEmailLanding:
               anchors rather than el-dropdown-item, the shared active-dot /
               inactive-dot span for the status toggle, text-default icons, and
               an <hr> above the destructive action. -->
          <el-dropdown>
            <base-button link type="primary">
              <i style="font-size: medium" class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-2 mb-2" href="#" @click.prevent="openEditor(row.item)">
                  <i class="tim-icons icon-pencil text-default mr-2"></i> Edit template
                </a>
                <a v-if="row.item.status !== 'archived'" class="dropdown-item mb-2" href="#" @click.prevent="toggleStatus(row.item)">
                  <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px"
                        :class="row.item.status === 'active' ? 'inactive-dot mr-2' : 'active-dot mr-2'"></span>
                  {{ row.item.status === 'active' ? 'Deactivate' : 'Activate' }}
                </a>
                <hr>
                <a class="dropdown-item" href="#" @click.prevent="confirmArchive(row.item)">
                  <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive template
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </b-table>
    </div>

    <!-- Mobile cards -->
    <div v-if="!loading && templates.length > 0" class="d-block d-sm-none">
      <div v-for="t in templates" :key="'m' + t.id" class="sms-templates__card" @click="openEditor(t)">
        <div class="sms-templates__card-head">
          <span class="sms-templates__name">{{ t.name }}</span>
          <badge :type="t.status === 'active' ? 'success' : 'default'">{{ t.status }}</badge>
        </div>
        <div class="sms-templates__meta">
          {{ t.type_name || 'General' }} &middot;
          {{ segmentsFor(t.body).segments }} segment{{ segmentsFor(t.body).segments === 1 ? '' : 's' }} &middot;
          {{ usageSummary(t) }}
        </div>
      </div>
    </div>

    <b-pagination
      v-if="templates.length > 0"
      v-model="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      @input="loadTemplates(perPage, getOffset())"
    ></b-pagination>

    <sms-template-drawer
      :show="editorOpen"
      :template="editing"
      :template-types="templateTypes"
      :saving="saving"
      :validation-errors="validationErrors"
      @close="closeEditor"
      @save="saveTemplate"
    />
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { API_LOCATION } from '@/api-config';
import { BTable, BPagination } from 'bootstrap-vue';
import { Dropdown, DropdownMenu } from 'element-ui';
import { Badge } from 'src/components';
import { calculateSegments } from '@/util/smsSegments';
import { formatGroupDate } from '@/plugins/dates';
import { downloadCsv, fetchAllPages } from '@/util/listPage';
import SMSTemplateDrawer from './SMSTemplateDrawer.vue';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Draft', value: 'draft' },
  { label: 'Archived', value: 'archived' }
];

export default {
  name: 'SMSTemplatesTab',
  components: {
    BTable,
    BPagination,
    // el-dropdown / el-dropdown-menu are not in globalComponents.js; unknown
    // el-* tags render inert with clicks falling through.
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    Badge,
    'sms-template-drawer': SMSTemplateDrawer
  },
  data() {
    // Seeded here, never in mounted(): FilterBar is a child, so its mount and
    // its initial `change` fire BEFORE this component's mounted() hook. Reading
    // the group there would leave companyGroupId null for the first fetch and
    // the list would silently never load.
    let companyGroupId = null;
    try {
      const group = JSON.parse(localStorage.getItem('group') || '{}');
      companyGroupId = group.id || null;
    } catch (e) {
      companyGroupId = null;
    }

    return {
      companyGroupId,
      loading: false,
      saving: false,
      templates: [],
      templateTypes: [],
      editorOpen: false,
      editing: null,
      validationErrors: [],
      companyId: localStorage.getItem('companyId'),
      // Pagination, matching the email templates tab.
      currentPage: 1,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50],
      totalRows: 0,
      filterValues: {
        name: '',
        template_type: [],
        status: '',
        created_date: null
      },
      tableFields: [
        { key: 'template_info', label: 'Template' },
        { key: 'type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'usage', label: 'Usage' },
        { key: 'segments', label: 'Length' },
        { key: 'last_modified', label: 'Modified' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  computed: {
    filterSchema() {
      return [
        { key: 'name', type: 'text', label: 'Template name', placeholder: 'Template name', operatorLabel: 'contains', primary: true },
        {
          key: 'template_type',
          type: 'multiselect',
          label: 'Template type',
          primary: true,
          // Filters by id, unlike the email tab which filters by name: the SMS
          // list lambda matches template_type_id, and ids survive a rename.
          options: (this.templateTypes || []).map((t) => ({ label: t.name, value: t.id }))
        },
        { key: 'status', type: 'select', label: 'Status', primary: true, options: STATUS_OPTIONS },
        { key: 'created_date', type: 'daterange', label: 'Created date', primary: true }
      ];
    },
    hasActiveFilters() {
      const f = this.filterValues;
      return Boolean(f.name) ||
        (Array.isArray(f.template_type) && f.template_type.length > 0) ||
        Boolean(f.status) ||
        Boolean(f.created_date && (f.created_date.from || f.created_date.to));
    }
  },
  mounted() {
    // FilterBar (saved views enabled) resolves precedence and drives the first
    // fetch via its initial `change` — do not fetch here, or the list loads twice.
    // Seed the flag so the empty state does not flash before that lands.
    this.loading = true;
  },
  methods: {
    async getAuthToken() {
      try {
        const data = await Auth.currentSession();
        return data.idToken.jwtToken;
      } catch (e) {
        return null;
      }
    },
    segmentsFor(body) {
      return calculateSegments(body || '');
    },
    formatDate(value) {
      if (!value) return '-';
      try {
        return formatGroupDate(value);
      } catch (e) {
        return String(value).slice(0, 10);
      }
    },
    typeBadgeStyle(item) {
      const color = item.type_color || '#e14eca';
      return { backgroundColor: color, borderColor: color };
    },
    getOffset() {
      return Number((this.currentPage * this.perPage) - this.perPage);
    },
    buildQueryParams(count, offset) {
      const params = new URLSearchParams();
      params.append('count', count);
      params.append('offset', offset);
      // Seeds this group's template types from the system rows on first visit.
      params.append('init', 'true');

      const f = this.filterValues;
      if (f.name) params.append('search', f.name);
      if (Array.isArray(f.template_type) && f.template_type.length > 0) {
        params.append('template_type', f.template_type.join(','));
      }
      if (f.status) {
        params.append('status', f.status);
        // Archived rows are hidden by default; asking for them explicitly must win.
        if (f.status === 'archived') params.append('include_archived', 'true');
      }
      if (f.created_date && f.created_date.from) params.append('created_date_from', f.created_date.from);
      if (f.created_date && f.created_date.to) params.append('created_date_to', f.created_date.to);

      return params.toString();
    },
    onFiltersChanged(values) {
      this.filterValues = { ...this.filterValues, ...values };
      // Reset to page one before computing the offset, so a narrowed filter
      // cannot leave us requesting a page that no longer exists.
      this.currentPage = 1;
      this.loadTemplates(this.perPage, this.getOffset());
    },
    onPerPageChange(n) {
      this.perPage = n;
      this.currentPage = 1;
      this.loadTemplates(this.perPage, this.getOffset());
    },
    async loadTemplates(count, offset) {
      if (!this.companyId || !this.companyGroupId) return;
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const query = this.buildQueryParams(count || this.perPage, offset || 0);
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates?${query}`;
        const response = await this.axios.get(url, { headers: { Authorization: token } });
        this.templates = response.data.templates || [];
        this.templateTypes = response.data.template_types || [];
        this.totalRows = response.data.total_rows || 0;
      } catch (err) {
        console.log(err);
        swal('Error', 'Could not load SMS templates. Please refresh the page.', 'error');
      } finally {
        this.loading = false;
      }
    },
    usageSummary(t) {
      // Same three states as the desktop Usage cell.
      const sent = `${t.sent_count || 0} sent`;
      if (typeof t.delivery_rate === 'number') return `${sent} · ${t.delivery_rate}% delivered`;
      return t.sent_count > 0 ? `${sent} · Delivery pending` : sent;
    },
    // Exports every template matching the current filters, not just the loaded
    // page, like the email templates export on the same landing page.
    async downloadCSV() {
      if (!this.totalRows) {
        this.$notify({
          type: 'info',
          message: 'There are no templates in the current view.',
          icon: 'tim-icons icon-alert-circle-exc'
        });
        return;
      }
      const columns = [
        { label: 'Template', value: (t) => t.name },
        { label: 'Type', value: (t) => t.type_name || 'General' },
        { label: 'Status', value: (t) => t.status },
        { label: 'Sent', value: (t) => t.sent_count || 0 },
        { label: 'Delivery rate', value: (t) => (typeof t.delivery_rate === 'number' ? `${t.delivery_rate}%` : '') },
        { label: 'Segments', value: (t) => this.segmentsFor(t.body).segments },
        { label: 'Characters', value: (t) => this.segmentsFor(t.body).length },
        { label: 'Modified', value: (t) => this.formatDate(t.updated_at || t.created_at) },
        { label: 'Message', value: (t) => t.body }
      ];
      try {
        const token = await this.getAuthToken();
        const base = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates`;
        // Batches of 100: the list lambda caps count at 100.
        const rows = await fetchAllPages(async (count, offset) => {
          const response = await this.axios.get(`${base}?${this.buildQueryParams(count, offset)}`, {
            headers: { Authorization: token }
          });
          return { rows: response.data.templates || [], total: response.data.total_rows || 0 };
        });
        downloadCsv('sms-templates.csv', columns, rows);
      } catch (err) {
        console.log(err);
        this.$notify({
          type: 'danger',
          message: 'Could not export SMS templates. Please try again.',
          icon: 'tim-icons icon-alert-circle-exc'
        });
      }
    },
    // The update endpoint replaces the whole record rather than patching, so the
    // unchanged fields go back with it. Sending only `status` would blank the
    // name and body.
    async toggleStatus(template) {
      const nextStatus = template.status === 'active' ? 'draft' : 'active';
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates/${template.id}`;
        await this.axios.put(url, {
          name: template.name,
          body: template.body,
          template_type_id: template.template_type_id,
          status: nextStatus
        }, { headers: { Authorization: token } });
        this.$notify({
          type: 'success',
          message: nextStatus === 'active' ? 'Template activated' : 'Template deactivated',
          icon: 'tim-icons icon-check-2'
        });
        await this.loadTemplates(this.perPage, this.getOffset());
      } catch (err) {
        const raw = err.response?.data;
        let parsed = raw;
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw); } catch (e) { parsed = { error: raw }; }
        }
        swal('Could not update', parsed?.error || 'Please try again.', 'error');
      }
    },
    openEditor(template = null) {
      this.validationErrors = [];
      this.editing = template ? { ...template } : null;
      this.editorOpen = true;
    },
    closeEditor() {
      this.editorOpen = false;
      this.editing = null;
      this.validationErrors = [];
    },
    async saveTemplate(payload) {
      if (!this.companyId || !this.companyGroupId) return;
      this.saving = true;
      this.validationErrors = [];
      try {
        const token = await this.getAuthToken();
        const base = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates`;
        const config = { headers: { Authorization: token } };

        if (payload.id) {
          await this.axios.put(`${base}/${payload.id}`, payload, config);
        } else {
          await this.axios.post(base, payload, config);
        }

        this.$notify({
          type: 'success',
          message: payload.id ? 'Template updated' : 'Template created',
          icon: 'tim-icons icon-check-2'
        });
        this.closeEditor();
        await this.loadTemplates(this.perPage, this.getOffset());
      } catch (err) {
        const raw = err.response?.data;
        let parsed = raw;
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw); } catch (e) { parsed = { error: raw }; }
        }
        if (Array.isArray(parsed?.validation_errors)) {
          this.validationErrors = parsed.validation_errors;
          this.$notify({
            type: 'danger',
            message: `Please fix ${parsed.validation_errors.length} ${parsed.validation_errors.length === 1 ? 'error' : 'errors'} below.`,
            icon: 'tim-icons icon-alert-circle-exc'
          });
        } else {
          swal('Could not save', parsed?.error || 'Please try again.', 'error');
        }
      } finally {
        this.saving = false;
      }
    },
    async confirmArchive(template) {
      const result = await swal({
        title: `Archive "${template.name}"?`,
        text: 'Automations using this template will stop sending it. Past messages keep their history.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Archive template',
        cancelButtonText: 'Cancel'
      });
      if (!result.value) return;

      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates/${template.id}`;
        await this.axios.delete(url, { headers: { Authorization: token } });
        this.$notify({ type: 'success', message: 'Template archived', icon: 'tim-icons icon-check-2' });
        // Archiving the last row on a page would otherwise strand us past the end.
        if (this.templates.length === 1 && this.currentPage > 1) this.currentPage -= 1;
        await this.loadTemplates(this.perPage, this.getOffset());
      } catch (err) {
        console.log(err);
        swal('Error', 'Could not archive the template. Please try again.', 'error');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sms-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
}
.sms-empty-state__hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 400px;
  margin: 0.25rem auto 1rem;
}

.sms-templates {
  &__name {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
  }

  &__meta,
  &__segments {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.8125rem;
  }

  &__card {
    padding: 14px 16px;
    border: 1px solid rgba(125, 125, 125, 0.18);
    border-radius: 0.375rem;
    margin-bottom: 10px;
    cursor: pointer;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 4px;
  }
}
</style>

<style>
.white-content .sms-templates__name { color: rgba(0, 0, 0, 0.9); }
.white-content .sms-templates__meta,
.white-content .sms-templates__segments { color: rgba(0, 0, 0, 0.65); }
.white-content .sms-empty-state__hint { color: rgba(0, 0, 0, 0.7); }
</style>
